import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import heart from "../../assets/svg/heart.svg";
import HeartFilled from "../../assets/svg/HeartFilled.svg";
import comment from "../../assets/svg/comment.svg";
import share from "../../assets/svg/share.svg";
import EP1 from "../../assets/images/EP1.jpg";
import Avatar from "../../components/Avatar/Avatar";
import axios from "../../components/Hooks/axios";
import { socket } from "../../App";
import { extractPostMedia, formatCount } from "./exploreUtils";
import ExplorePostCard from "./ExplorePostCard";
import styles from "./Explore.module.scss";

const ExploreYourMind = ({ posts = [], isLoading = false }) => {
	const navigate = useNavigate();
	const { userId, user } = useSelector((state) => state.auth);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 427);

	const heroPost = posts[0];
	const [isLiked, setIsLiked] = useState(false);
	const [likeCount, setLikeCount] = useState(0);
	const [isFollowing, setIsFollowing] = useState(false);
	const [heroImgError, setHeroImgError] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 427);
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		if (heroPost) {
			const initialLikes = heroPost.likes?.length !== undefined
				? heroPost.likes.length
				: heroPost.LikeCount !== undefined
				? heroPost.LikeCount
				: 0;
			setLikeCount(initialLikes);
			setIsLiked(Boolean(heroPost.isLike));
			setIsFollowing(Boolean(heroPost.isFollowing));
			setHeroImgError(false);
		}
	}, [heroPost]);

	const heroMedia = heroPost ? extractPostMedia(heroPost) : null;
	const heroAuthor = heroPost?.userId || {};
	const heroAuthorName = heroAuthor.username || heroAuthor.displayName || heroAuthor.GetstatedName || "User";
	const heroAuthorHandle = heroAuthor.GetstatedName || heroAuthor.username || "user";

	const handleFollow = (e) => {
		e.stopPropagation();
		if (!heroAuthor._id || heroAuthor._id === userId) return;

		axios
			.post("/auth/follow", { following: heroAuthor._id })
			.then(() => {
				setIsFollowing((prev) => !prev);
				toast.success(
					isFollowing
						? `Unfollowed ${heroAuthorName}`
						: `You are now following ${heroAuthorName}`
				);
			})
			.catch((err) => {
				console.error("Follow error:", err);
			});
	};

	const handleLike = (e) => {
		e.stopPropagation();
		if (!heroPost?._id || !userId) return;

		const prevLiked = isLiked;
		const prevCount = likeCount;

		// Optimistic UI update
		setIsLiked(!prevLiked);
		setLikeCount(prevLiked ? Math.max(0, prevCount - 1) : prevCount + 1);

		axios
			.post(`/post/${heroPost._id}/likeANDunlike`, { userId })
			.then(({ data }) => {
				const serverIsLike = data?.totalLikes?.[0]?.isLike;
				const serverCount = data?.totalLikes?.[0]?.LikeCount;
				if (serverIsLike !== undefined) setIsLiked(serverIsLike);
				if (serverCount !== undefined) setLikeCount(serverCount);

				if (serverIsLike && socket && heroAuthor._id) {
					socket.emit("notification", {
						notification_from: userId,
						notification_to: heroAuthor._id,
						notification: {},
						title: "Like",
						message: `${user?.username || "Someone"} liked your post`,
					});
				}
			})
			.catch((err) => {
				console.error("Like error:", err);
				setIsLiked(prevLiked);
				setLikeCount(prevCount);
			});
	};

	const handleShare = (e) => {
		e.stopPropagation();
		if (!heroPost?._id) return;
		const shareUrl = `${window.location.origin}/Post/${heroPost._id}`;

		if (navigator.share) {
			navigator
				.share({
					title: `Fameo - Post by ${heroAuthorName}`,
					text: heroPost.caption || "Check out this post on Fameo!",
					url: shareUrl,
				})
				.catch(() => {});
		} else if (navigator.clipboard) {
			navigator.clipboard
				.writeText(shareUrl)
				.then(() => toast.success("Post link copied to clipboard!"))
				.catch(() => toast.info(shareUrl));
		}
	};

	const commentsLength =
		heroPost?.comments?.length !== undefined
			? heroPost.comments.length
			: heroPost?.Comments?.length !== undefined
			? heroPost.Comments.length
			: 0;

	return (
		<>
			<h1 className={styles.sectionHeading}>Explore your mind</h1>

			{/* Hero Post Display */}
			{heroPost && heroMedia && (
				<div
					className={styles.ExploreMindCont1}
					onClick={() => navigate(`/Post/${heroPost._id}`)}
					style={{ cursor: "pointer" }}
				>
					{heroMedia.isVideo && !heroImgError ? (
						<video
							src={heroMedia.url}
							className={styles.heroMedia}
							autoPlay
							muted
							loop
							playsInline
							onError={() => setHeroImgError(true)}
						/>
					) : (
						<img
							src={heroImgError || !heroMedia.url ? EP1 : heroMedia.url}
							alt={heroPost.caption || "Explore your mind"}
							className={styles.heroMedia}
							onError={() => setHeroImgError(true)}
						/>
					)}

					<div className={styles.ExploreMindCont1Overlay} />

					<div className={styles.MindTop}>
						<div
							onClick={(e) => {
								e.stopPropagation();
								navigate(`/Post/${heroPost._id}`);
							}}
						>
							<div className={styles.MindTopAvatar}>
								<Avatar
									src={heroAuthor.ProfilePic || heroAuthor.personaThumbnail}
									name={heroAuthorName}
									style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
								/>
							</div>
							<h5>
								<p>{heroAuthorName}</p>
								<span>@{heroAuthorHandle}</span>
							</h5>
						</div>

						{userId !== heroAuthor._id && (
							<button
								type="button"
								className={`${styles.followButton} ${isFollowing ? styles.following : ""}`}
								onClick={handleFollow}
							>
								{isFollowing ? "Following" : "Follow"}
							</button>
						)}
					</div>

					<div className={styles.MindBottom}>
						<p className={styles.commentText} title={heroPost.caption || ""}>
							{heroPost.caption || "No caption"}
						</p>

						<div className={styles.actionContainer}>
							<div className={styles.h} onClick={handleLike} title="Like">
								<img
									src={isLiked ? HeartFilled : heart}
									alt="like"
									className={isLiked ? styles.heartFilledIcon : ""}
								/>
								<p>{formatCount(likeCount)}</p>
							</div>

							<div
								className={styles.c}
								onClick={(e) => {
									e.stopPropagation();
									navigate(`/Post/${heroPost._id}`);
								}}
								title="Comment"
							>
								<img src={comment} alt="comment" />
								<p>{formatCount(commentsLength)}</p>
							</div>

							<div className={styles.s} onClick={handleShare} title="Share">
								<img src={share} alt="share" />
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Recommend for you Grid (Remaining Posts) */}
			<div className={styles.ExploreImageMainContainer}>
				{isMobile ? null : <h3>Recommend for you</h3>}
				<div className={styles.ExploreImageContainer}>
					{posts.slice(1).map((item, index) => (
						<ExplorePostCard
							key={item._id || index}
							post={item}
							variant="grid"
							className={
								index === 0
									? styles.row2
									: index === 1
									? styles.row21
									: ""
							}
						/>
					))}
				</div>
			</div>
		</>
	);
};

export default ExploreYourMind;
